import { BOOKING_URL, trackBooking } from "@/lib/booking";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { CATEGORIES, STOTRAS } from "@/data/stotras";
import { useSeo } from "@/lib/seo";
import { SiteHeader } from "@/components/SiteHeader";

export { SiteHeader as StotraHeader };

const Card = ({ s }) => (
  <Link to={`/stotras/${s.slug}`} data-testid={`stotra-card-${s.slug}`}
    className="group flex h-full flex-col overflow-hidden rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] shadow-sm transition-shadow hover:shadow-md">
    <div className="h-48 overflow-hidden bg-[#F3EADA]/50 flex items-center justify-center">
      <img src={s.img} alt={s.titleEn} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
    </div>
    <div className="flex flex-1 flex-col p-4">
      <p className="font-baloo text-base leading-snug text-[#A8722A]">{s.titleKn}</p>
      <h3 className="mt-0.5 font-eczar text-base font-semibold leading-snug text-[#3B221B]">{s.titleEn}</h3>
      <p className="mt-1.5 flex-1 font-mukta text-sm text-[#6B5044]">{s.blurb}</p>
      <span className="mt-3 font-mukta text-sm font-semibold text-[#B23A2B]">Read →</span>
    </div>
  </Link>
);

export default function StotraHub() {
  const [q, setQ] = useState("");
  useSeo({
    title: "Stotras & Mantras in Kannada and English — PurohithConnect",
    description: "Read Vishnu Sahasranama, Lalitha Sahasranama, Ganesha Ashtottara, Sandhyavandane, daily shlokas and festival stotras — full text in Kannada script with English transliteration.",
    canonicalPath: "/stotras",
    jsonLd: useMemo(() => ({
      "@context": "https://schema.org", "@type": "CollectionPage",
      name: "Stotra Library — PurohithConnect",
      hasPart: STOTRAS.map((s) => ({ "@type": "CreativeWork", name: s.titleEn, url: `https://purohithconnect.com/stotras/${s.slug}` })),
    }), []),
  });
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return null;
    return STOTRAS.filter((s) => (s.titleEn + s.titleKn + s.deity + s.blurb).toLowerCase().includes(t));
  }, [q]);

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <SiteHeader />
      <section className="relative">
        <img src="/stotra-art/hub-hero.jpg" alt="Palm-leaf manuscript with diya" className="h-56 w-full object-cover md:h-72" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FBF6EC] via-[#FBF6EC]/40 to-transparent" />
      </section>
      <main className="mx-auto max-w-6xl px-4 pb-20 md:px-8">
        <div className="-mt-16 relative text-center">
          <p className="font-baloo text-base text-[#B23A2B]">|| ಶ್ರೀ ಗುರುಭ್ಯೋ ನಮಃ ||</p>
          <h1 className="mt-1 font-eczar text-4xl font-bold sm:text-5xl">Stotra Library</h1>
          <p className="mt-1 font-baloo text-lg text-[#A8722A]">ಸ್ತೋತ್ರ ಮಾಲಾ · ಕನ್ನಡ ಮತ್ತು ಇಂಗ್ಲಿಷ್</p>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044]">
            Sahasranamas, ashtottaras, sandhyavandane and everyday shlokas — complete texts in Kannada script with English transliteration, for daily parayana and festival poojas.
          </p>
          <div className="relative mx-auto mt-6 max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8722A]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stotras… (e.g. Lakshmi, Sahasranama)"
              aria-label="Search stotras" data-testid="stotra-search-input"
              className="w-full rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] py-3 pl-11 pr-4 font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50" />
          </div>
          <Link to="/rituals" data-testid="stotra-to-rituals"
            className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-[#B23A2B]/40 bg-[#B23A2B]/[0.06] px-5 py-2 font-mukta text-sm font-semibold text-[#B23A2B] transition-colors hover:bg-[#B23A2B] hover:text-white">
            New: Rituals &amp; Vratas — Sandhyavandane, Upakarma &amp; South-Indian vratas →
          </Link>
        </div>

        {filtered ? (
          <section className="mt-10">
            <h2 className="font-eczar text-xl font-semibold">Search results ({filtered.length})</h2>
            <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((s) => <Card key={s.slug} s={s} />)}
            </div>
          </section>
        ) : (
          CATEGORIES.map((cat) => {
            const items = STOTRAS.filter((s) => s.category === cat.key);
            return (
              <section key={cat.key} className="mt-12" data-testid={`stotra-category-${cat.key}`}>
                <div className="flex items-baseline gap-3">
                  <h2 className="font-eczar text-2xl font-semibold">{cat.en}</h2>
                  <span className="font-baloo text-lg text-[#A8722A]">{cat.kn}</span>
                </div>
                <p className="mt-1 font-mukta text-sm text-[#6B5044]">{cat.desc}</p>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((s) => <Card key={s.slug} s={s} />)}
                </div>
              </section>
            );
          })
        )}

        <div className="mt-16 rounded-[14px] border border-[#A8722A]/30 bg-[#F3EADA] p-6 text-center">
          <p className="font-eczar text-lg font-semibold">Want a purohith to perform the full pooja at your home?</p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "stotra-hub" })} className="mt-3 inline-block rounded-full bg-[#B23A2B] px-6 py-2.5 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]" data-testid="stotra-hub-cta">
            Book a Purohith ↗
          </a>
        </div>
      </main>
      <footer className="border-t border-[#A8722A]/20 bg-[#F3EADA] py-5 text-center font-mukta text-sm text-[#6B5044]">
        © 2026 PurohithConnect · Built in Bengaluru, Karnataka — for India
      </footer>
    </div>
  );
}
